import { navigate } from 'gatsby';
import React, { use } from 'react';

import type { PageLanguage } from 'src/components/common/page-context';
import { SiteContext } from 'src/context/site-context';
import type { BuildType } from 'src/enums/build/build-type';
import { shouldIncludeLanguageInUrlForBuildType } from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import { getUrlValueForLanguage } from 'src/enums/language';
import { preparePath } from 'src/helpers/navigation-helper';
import { trimPath } from 'src/helpers/path-matching-helper';
import { updateUrlPreservingQueryParams } from 'src/helpers/query-string-helper';
import { shadow, zIndexes } from 'src/styling/constants';

import LanguageItem from './language-item';

const navigateToPathForLanguage = (language: Language, path: string, buildType: BuildType) => {
  const trimmedPath = trimPath(path);
  const pathWithLanguage = shouldIncludeLanguageInUrlForBuildType(buildType)
    ? `/${getUrlValueForLanguage(language)}/${trimmedPath}/`
    : `/${trimmedPath}/`;

  navigate(updateUrlPreservingQueryParams(preparePath(pathWithLanguage)));
};

type LanguagePickerPopupProps = {
  currentLanguage: Language;
  pageLanguages: PageLanguage[];
};

const LanguagePickerPopup: React.FC<LanguagePickerPopupProps> = ({
  currentLanguage,
  pageLanguages,
}) => {
  const { styling, buildType, isMeridian } = use(SiteContext);
  return (
    <div
      css={[
        shadow.regular,
        { position: 'absolute', background: 'white', top: '-0.5rem', zIndex: zIndexes.overlay },
      ]}
    >
      <div
        css={[
          {
            color: isMeridian ? styling.colors.capeCod : styling.colors.highlightText,
          },
          isMeridian && {
            '&:hover': { color: styling.colors.meridianBlue, textDecoration: 'underline' },
          },
        ]}
      >
        <LanguageItem language={currentLanguage} />
      </div>
      {pageLanguages
        .filter(({ language }) => language !== currentLanguage)
        .map(({ language, path }) => (
          <div
            key={language}
            css={[
              {
                color: isMeridian ? styling.colors.capeCod : styling.colors.bodyText,
              },
              isMeridian && {
                '&:hover': { color: styling.colors.meridianBlue, textDecoration: 'underline' },
              },
            ]}
            onClick={() => navigateToPathForLanguage(language, path, buildType)}
          >
            <LanguageItem language={language} />
          </div>
        ))}
    </div>
  );
};

export default LanguagePickerPopup;
